// app.component.ts
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { NotificationService } from './services/notification/notification.service';
import { PushService } from './services/push/push.service';
import { Platform } from '@angular/cdk/platform';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { SplashScreenService } from './services/splash-screen.service';
import { PullToRefreshService } from './services/pull-to-refresh.service';
import { DeepLinkService } from './services/deep-link.service';
import { AppExitService } from './services/app-exit/app-exit.service';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { ShepherdService } from './core/services/shepherd/shepherd.service';
import { PostAuthActivityService } from './services/post-auth-activity.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    IonicModule,
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  isDesktop: boolean = false;
  isLoaded: boolean = false;
  isNativePlatform: boolean = false;
  isNotificationsSupported: boolean = false;
  environment = environment;
  private notificationSub: Subscription;
  private routerSubscription: Subscription;

  private notifService = inject(NotificationService);
  private pushService = inject(PushService);
  private appExitService = inject(AppExitService);
  private platform = inject(Platform);
  private splashScreenService = inject(SplashScreenService);
  readonly pullToRefreshService = inject(PullToRefreshService);
  private deepLinkService = inject(DeepLinkService);

  constructor(
    private router: Router,
    private shepherd: ShepherdService,
    private postAuthActivity: PostAuthActivityService,
  ) {
    this.initializeApp();
    // Subscribe to notifications
    this.notificationSub = this.notifService.notificationReceived$.subscribe(
      notification => {
        // Handle the notification in your UI
      }
    );
    
    // Subscribe to route changes and scroll to top
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event) => {
        window.scrollTo(0, 0);
        const path = (event as NavigationEnd).urlAfterRedirects.split('?')[0];
        if (path.includes('explore-activity')) {
          this.shepherd.tryStartExploreActivityTourForTesting();
        } else if (path.includes('add-activity')) {
          this.shepherd.tryStartPostActivityTourForAddActivityRoute();
        } else if (
          path === '/' ||
          path === '' ||
          path === '/home' ||
          path.startsWith('/home/')
        ) {
          if (!this.postAuthActivity.hasPendingOpenOnHome()) {
            this.shepherd.tryStartHomePageTourForTesting();
          }
        } else if (path.includes('messages')) {
          this.shepherd.tryStartMessagesConnectionsTour();
        } else if (path === '/training' || path === '/training/') {
          this.shepherd.stopTour();
          this.shepherd.tryStartTrainingBookTrainerTour();
        } else if (path.startsWith('/training/book')) {
          // Leave Shepherd running for `training-book-session` on booking page.
        } else if (path === '/schedule' || path === '/schedule/') {
          this.shepherd.stopTour();
          this.shepherd.tryStartSchedulePageTour();
        } else {
          this.shepherd.stopTour();
        }
      });
  }

  private async initializeApp() {
    try {
      // Check if we're in a native environment
      if (typeof window !== 'undefined' && 'Capacitor' in window) {
        await this.configureStatusBarForTheme();
        // Initialize splash screen for native platforms
        await this.splashScreenService.initializeSplashScreen();
      }
    } catch (error) {
      console.error('App initialization failed:', error);
    }
  }

  /** White/light status bar icons (Capacitor Style.DARK) so icons stay visible on themed dark headers. */
  private async configureStatusBarForTheme(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      await StatusBar.setStyle({ style: Style.Dark });
    } catch {
      // Web / unsupported: ignore
    }
  }

  async ngOnInit() {
    try {
      // Set loaded to true immediately to prevent white screen
      this.isLoaded = true;
      
      // Ensure splash screen is visible for a minimum duration
      await this.ensureSplashScreenVisibility();

      // Deep linking: custom scheme + universal links (cold start + background)
      this.deepLinkService.init();
      this.appExitService.init();
      if (Capacitor.isNativePlatform()) {
        // Do not await — permission/register must not block first paint.
        void this.pushService.initPush();
        this.isNotificationsSupported = true;
      } else {
        this.isNotificationsSupported =
          !this.platform.SAFARI &&
          typeof window !== 'undefined' &&
          typeof (window as any).Notification !== 'undefined';
      }

      // Push notification click may arrive before Router is ready
      setTimeout(() => this.notifService.consumePendingPushNavigation(), 300);

      this.isNotificationsSupported = !this.platform.IOS && !this.platform.SAFARI;
    } catch (error) {
      console.error('App ngOnInit failed:', error);
      // Still set loaded to true to prevent white screen
      this.isLoaded = true;
    }
  }

  private async ensureSplashScreenVisibility() {
    try {
      this.isNativePlatform = this.splashScreenService.isNative();
      
      if (this.isNativePlatform) {
        
        // Hide splash screen after a short delay (don't block the app)
        setTimeout(async () => {
          try {
            await this.splashScreenService.hideSplashScreen();
          } catch (error) {
            console.error('Error hiding splash screen:', error);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error managing splash screen visibility:', error);
      this.isNativePlatform = false;
    }
  }

  onPullRefresh(): void {
    this.pullToRefreshService.triggerRefresh();
  }

  /** Clears completed tours, visit counts, and training session tour pending state (non-production + shepherd flag only). */
  resetShepherdToursForQa(): void {
    this.shepherd.resetAllTours();
  }

  // Method to be called from a user interaction (like button click)
  async initializeNotifications() {
    try {
      if (Capacitor.isNativePlatform()) {
        await this.pushService.initPush();
      } else if (this.isNotificationsSupported) {
        await this.notifService.initializeNotifications();
      }
    } catch (error) {
      console.error('Error setting up notifications:', error);
    }
  }

  ngOnDestroy() {
    void this.appExitService.destroy();   
     if (this.notificationSub) {
      this.notificationSub.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
